import { Component, ElementRef, Inject, LOCALE_ID, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { TicketDetailsComponent } from '../ticket-details/ticket-details.component';
import { ModalController, NavController } from '@ionic/angular';
import Env, { PRODUCT_AREA_DISPLAY_OPTION } from '@ridango/core/env';
import { AreaMapService } from '@ridango/core/lib/services/area-map.service';
import { FormBuilder } from '@angular/forms';
import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    FavoritesFacade,
    TicketsService,
    Ticket,
    ProductsFacade,
    ProductsService
} from '@ridango/components';
import {
    AppStartupService,
    AuthService,
    CoreFacade,
    NetworkService,
    TranslateObjectFieldPipe,
    ValidityTimerData
} from '@ridango/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { ScreenOrientationService } from '@ridango/core/lib/services/screenOrientation.service';
import { DateCalculationService } from '../../services/date-calculation.service';
import { TicketActionsService } from '../../services/ticket-actions/ticket-actions.service';
import { TranslateService } from '@ngx-translate/core';
import { TicketQrVisibilityService } from '../../services/ticket-qr-visibility.service';
import { FareContractStatus } from '@ridango/components/lib/account-tickets-ngrx/models/ticket-status.model';
import { TicketDetailsZoneMapComponent } from '../ticket-details/ticket-details-zone-map/ticket-details-zone-map.component';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { Observable, interval, of } from 'rxjs';
import { map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import {
    resolveTicketProgressBarTimeTextPlacement,
    TicketProgressBarTimeTextPlacement
} from '../../enums/ticket-progress-indicator.enum';

@Component({
    selector: 'rwidget-ticket-details-v3',
    templateUrl: './ticket-details-v3.component.html',
    styleUrls: ['./ticket-details-v3.component.scss']
})
export class TicketDetailsV3Component extends TicketDetailsComponent implements OnChanges {
    protected readonly FareContractStatus = FareContractStatus;
    protected readonly PRODUCT_AREA_DISPLAY_OPTION = PRODUCT_AREA_DISPLAY_OPTION;
    protected readonly productAreaDisplayOption =
        this.env.AREA_MAP?.PRODUCT_AREA_DISPLAY_OPTION ?? PRODUCT_AREA_DISPLAY_OPTION.FULL_MAP;

    @ViewChild('ticketTitleEl') ticketTitleEl: ElementRef<HTMLElement>;
    accordionValue?: string;
    travellers = '';
    currentLang = '';
    usedPaymentMethodsList = '';
    showAnimation$ = this.qrVisibilityService.isCodeVisible$;
    showDetails = false;
    hasZoneMapImage = false;
    isOnline$ = this.networkService.getStatus();
    ticketTimer$: Observable<ValidityTimerData | null>;

    constructor(
        @Inject('ENV') env: Env,
        @Inject(LOCALE_ID) locale: string,
        modalCtrl: ModalController,
        formBuilder: FormBuilder,
        accountTicketsFacade: AccountTicketsNgrxFacade,
        applicationFacade: ApplicationFacade,
        coreFacade: CoreFacade,
        appStartupService: AppStartupService,
        ticketService: TicketsService,
        private readonly ticketsFacade: AccountTicketsNgrxFacade,
        bpObservable: BreakpointObserver,
        favoritesFacade: FavoritesFacade,
        authService: AuthService,
        router: Router,
        activatedRoute: ActivatedRoute,
        translate: TranslateService,
        networkService: NetworkService,
        analyticsService: AnalyticsService,
        screenOrientationService: ScreenOrientationService,
        dateCalculationService: DateCalculationService,
        ticketActionsService: TicketActionsService,
        nav: NavController,
        translatePipe: TranslateObjectFieldPipe,
        private readonly qrVisibilityService: TicketQrVisibilityService,
        private readonly areaMapService: AreaMapService,
        productsFacade: ProductsFacade,
        productsService: ProductsService
    ) {
        super(
            env,
            locale,
            modalCtrl,
            formBuilder,
            accountTicketsFacade,
            applicationFacade,
            coreFacade,
            appStartupService,
            ticketService,
            bpObservable,
            favoritesFacade,
            authService,
            router,
            activatedRoute,
            translate,
            networkService,
            analyticsService,
            screenOrientationService,
            dateCalculationService,
            ticketActionsService,
            nav,
            translatePipe,
            productsFacade,
            productsService
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.ticket) {
            this.travellers = this.getTicketTravellers();
            this.currentLang = this.translate.currentLang;
            this.usedPaymentMethodsList = this.getUsedPaymentMethodsList();
            void this.updateZoneMapAvailability();
            this.initializeTicketProgressTimer();
        }
    }

    private getTicketTravellers(): string {
        const isGroup = this.ticket?.userProfiles?.[0]?.group;

        return isGroup
            ? this.ticketService.getSelectedTravellerTypesOnTicket(
                  this.ticket?.userProfiles?.[0]?.companionProfiles,
                  this.ticket?.dynamicProductParameters
              )
            : this.translateString(this.ticket?.userProfiles?.[0]);
    }

    get showTicketDetailsProgressBar(): boolean {
        return this.isTicketDisplayedAsActive && this.isTicketProgressIndicatorEnabled;
    }

    get showTicketDetailsProgressTimeText(): boolean {
        return this.ticketDetailsTimeTextPlacement !== 'hidden';
    }

    get isTicketDetailsProgressTimeTextOnRight(): boolean {
        return this.ticketDetailsTimeTextPlacement === 'right';
    }

    isMultiTrip(ticket: Ticket): boolean {
        return ticket?.typeOfProduct?.code === 'MULTI_TRIP';
    }

    get isTicketNotCurrentlyValid(): boolean {
        return this.ticket?.status === FareContractStatus.ACTIVE && this.ticket?.allowedActions?.validable === false;
    }

    get isTicketDisplayedAsActive(): boolean {
        return this.ticket?.status === FareContractStatus.ACTIVE && !this.isTicketNotCurrentlyValid;
    }

    reactivateTicket(ticket: Ticket): void {
        this.ticketsFacade.reactivateTicket(String(ticket.id ?? ticket.docNum));
        this.refreshTickets.emit();
    }

    toggleExpandCollapseDetailedInfo(): void {
        this.showDetails = !this.showDetails;
        this.accordionValue = this.showDetails ? 'detailsShown' : null;

        if (this.showDetails) {
            this.ticketTitleEl.nativeElement.focus();
        }
    }

    isManualActivationEnabled(ticket: Ticket): boolean {
        return (
            this.env.TICKET_MANUAL_ACTIVATION_ALLOWED &&
            !ticket.startDate &&
            !ticket.endDate &&
            ticket.type === 'SINGLE_PRODUCT' &&
            ticket.status === 'CONFIRMED'
        );
    }

    isMenuReactivationEnabled(ticket: Ticket): boolean {
        const AWAITING_REACTIVATION = 'AWAITING_REACTIVATION';
        return this.env.TICKET_ACTIONS?.includes(AWAITING_REACTIVATION) && ticket.status === AWAITING_REACTIVATION;
    }

    async openZoneMapFromGeography(): Promise<void> {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: 'Ticket details - Zone map'
        });

        const imageSrc = await this.getZoneMapImageSrc();
        if (!imageSrc) {
            return;
        }

        const modal = await this.modalCtrl.create({
            component: TicketDetailsZoneMapComponent,
            componentProps: { imageSrc },
            cssClass: 'ticket-details-zone-map-modal'
        });

        await modal.present();
    }

    private async updateZoneMapAvailability(): Promise<void> {
        const zoneGroupValue = this.ticket?.geography?.zones?.[0]?.code ?? null;
        if (zoneGroupValue === null || zoneGroupValue === undefined) {
            this.hasZoneMapImage = false;
            return;
        }

        const imageSrc = await this.areaMapService.resolveAreaMapImageSrc(zoneGroupValue);
        this.hasZoneMapImage = Boolean(imageSrc);
    }

    private async getZoneMapImageSrc(): Promise<string | null> {
        const zoneGroupValue = this.ticket?.geography?.zones?.[0]?.code ?? null;
        return this.areaMapService.resolveAreaMapImageSrc(zoneGroupValue);
    }

    private get isTicketProgressIndicatorEnabled(): boolean {
        const indicatorConfig = this.env.TICKET_PROGRESS_INDICATOR;
        return Boolean(indicatorConfig) && indicatorConfig.SHOW_IN_TICKET_DETAILS !== false;
    }

    private get ticketDetailsTimeTextPlacement(): TicketProgressBarTimeTextPlacement {
        const placement = this.env.TICKET_PROGRESS_INDICATOR?.TICKET_DETAILS_TIME_LABEL_PLACEMENT;

        return resolveTicketProgressBarTimeTextPlacement(placement);
    }

    private get isTicketDetailsHMinTimeFormatEnabled(): boolean {
        return this.env.TICKET_PROGRESS_INDICATOR?.TICKET_DETAILS_USE_H_MIN_FORMAT === true;
    }

    private initializeTicketProgressTimer(): void {
        if (!this.isTicketDisplayedAsActive) {
            this.ticketTimer$ = of(null);
            return;
        }

        this.ticketTimer$ = interval(60000).pipe(
            takeUntil(this.componentDestroyed$),
            startWith(0),
            map(() => this.calculateTicketTimer()),
            shareReplay(1)
        );
    }

    private calculateTicketTimer(): ValidityTimerData | null {
        const endingDate = this.ticket?.endDate ?? this.ticket?.ending;

        if (!endingDate) {
            return null;
        }

        const endTime = new Date(endingDate);
        if (Number.isNaN(endTime.getTime())) {
            return null;
        }

        const now = new Date();
        const remainingMs = endTime.getTime() - now.getTime();

        if (remainingMs <= 0) {
            return {
                remainingTimeText: '00:00',
                progressValue: 0,
                isExpired: true
            };
        }

        const startDate = this.ticket?.startDate ?? this.ticket?.starting;
        if (!startDate) {
            return {
                remainingTimeText: this.formatRemainingTime(remainingMs),
                progressValue: 100,
                isExpired: false
            };
        }

        const startTime = new Date(startDate);
        if (Number.isNaN(startTime.getTime())) {
            return {
                remainingTimeText: this.formatRemainingTime(remainingMs),
                progressValue: 100,
                isExpired: false
            };
        }

        const totalDurationMs = endTime.getTime() - startTime.getTime();

        if (totalDurationMs <= 0) {
            return {
                remainingTimeText: this.formatRemainingTime(remainingMs),
                progressValue: 100,
                isExpired: false
            };
        }

        return {
            remainingTimeText: this.formatRemainingTime(remainingMs),
            progressValue: Math.min(Math.max((remainingMs / totalDurationMs) * 100, 0), 100),
            isExpired: false
        };
    }

    private formatRemainingTime(remainingMs: number): string {
        const totalMinutes = Math.max(Math.floor(remainingMs / 60000), 0);
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;

        if (days > 0) {
            const dayLabel = days === 1 ? 'DAY' : 'DAYS';
            return `${days} ${this.translate.instant(dayLabel)}`;
        }

        if (this.isTicketDetailsHMinTimeFormatEnabled) {
            return `${this.formatDurationUnit(hours, 'hour')} ${this.formatDurationUnit(minutes, 'minute')}`;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    private formatDurationUnit(value: number, unit: 'hour' | 'minute'): string {
        const locale = this.translate.currentLang || 'en';

        try {
            return new Intl.NumberFormat(locale, {
                style: 'unit',
                unit,
                unitDisplay: 'narrow'
            } as Intl.NumberFormatOptions).format(value);
        } catch {
            const fallbackSuffix = unit === 'hour' ? 'h' : 'min';
            return `${value.toString().padStart(2, '0')}${fallbackSuffix}`;
        }
    }
    private getUsedPaymentMethodsList(): string {
        return (
            this.ticket?.distributionAssignment?.paymentMethods
                ?.map((paymentMethod) => {
                    const translations = paymentMethod.name?.translations;

                    if (!translations) return null;

                    return translations[this.currentLang] || translations[Object.keys(translations)[0]] || null;
                })
                .filter(Boolean)
                .join(' + ') ?? ''
        );
    }
}
